import { BuildingWithConvertedAddresses, LatLng } from '../../../utils/types';
import { AddMyPlaceFormState } from './types';
import {
  ListingAddressObject,
  ListingBuildingGeometry,
} from './resolveListingBuilding';

// The precise listing data captured from a map-confirmed building, so a listing
// can be created/moved without re-querying Mapbox.
export type ClickedBuildingState = {
  buildingId: string;
  addressObject: ListingAddressObject;
  geometry: ListingBuildingGeometry | null;
} | null;

// Treat a code point as a letter only when it has distinct lower/upper forms,
// which also covers Finnish/accented letters (ä, ö, å) without needing unicode
// regex support. Uppercases the first such letter, preserving the rest.
function capitalizeFirstLetter(part: string): string {
  for (let i = 0; i < part.length; i += 1) {
    const char = part[i];
    if (char.toLowerCase() !== char.toUpperCase()) {
      return part.slice(0, i) + char.toUpperCase() + part.slice(i + 1);
    }
  }
  return part;
}

// Uppercase the first letter of each comma-separated part (street, city,
// country) of a selected address, so a manually typed lowercase entry still
// reads as a proper place name. Only the first letter of each part is changed.
export const capitalizeAddressParts = (value: string): string =>
  value.split(',').map(capitalizeFirstLetter).join(',');

// Map a map-clicked building to the listing state both the create flow and the
// edit modal need: a form patch (address/area/postal), the listing point
// (centroid), and the structured `clickedBuilding` used to POST/PUT a real
// off-market willingness. Nullable fields let callers keep prior state when the
// clicked building lacks a centroid or coordinates.
export function buildingToListingState(building: BuildingWithConvertedAddresses): {
  formPatch: Partial<AddMyPlaceFormState>;
  listingLocation: LatLng | null;
  clickedBuilding: ClickedBuildingState;
} {
  const addr = building.addresses?.[0] ?? building.properties?.addresses?.[0];

  let formPatch: Partial<AddMyPlaceFormState> = {};
  if (addr) {
    const street = [addr.street, addr.houseNumber].filter(Boolean).join(' ').trim();
    formPatch = {
      address: capitalizeAddressParts(street || building.properties?.name || ''),
      area: capitalizeAddressParts(addr.city || ''),
      postal: addr.postcode || '',
    };
  } else if (building.properties?.name) {
    formPatch = { address: capitalizeAddressParts(building.properties.name) };
  }

  const listingLocation = building.centroid ?? null;

  let clickedBuilding: ClickedBuildingState = null;
  if (addr?.coordinates) {
    clickedBuilding = {
      buildingId: building.id,
      addressObject: {
        country: addr.country || '',
        region: addr.region || '',
        city: addr.city || '',
        postcode: addr.postcode || '',
        street: addr.street || '',
        houseNumber: addr.houseNumber || '',
        coordinates: addr.coordinates,
      },
      geometry: building.geometry as ListingBuildingGeometry | null,
    };
  }

  return { formPatch, listingLocation, clickedBuilding };
}
